import { FC, useContext } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ConnectorFamily } from "../../../../http/deckDesigner/types/connectorFamilies";
import { ConnectorMaterial } from "../../../../http/deckDesigner/types/connectorMaterials";
import { Connector, InsertConnectorBody, UpdateConnectorBody } from "../../../../http/deckDesigner/types/connectors";
import { Screw } from "../../../../http/deckDesigner/types/screws";
import useDeckConnectorFamiliesApi from "../../../../http/deckDesigner/useConnectorFamilies";
import useDeckConnectorMaterialsApi from "../../../../http/deckDesigner/useConnectorMaterials";
import useDeckConnectorsApi from "../../../../http/deckDesigner/useConnectors";
import useDeckScrewsApi from "../../../../http/deckDesigner/useScrews";
import useItemOptions, { getItemOptionLabel, ItemOption, toItemOption } from "../../../../hooks/useItemOptions";

interface ConnectorFormBody {
  item: ItemOption | null;
  connectorFamily: ConnectorFamily | null;
  connectorMaterial: ConnectorMaterial | null;
  thickness: number | null;
  screw: Screw | null;
  minGroovingHeight: number | null;
  minBoardWidth: number | null;
}

interface ConnectorFormProps {
  connector?: Connector;
  onClose: () => void;
}

interface ScrewFieldProps {
  control: Control<ConnectorFormBody>;
  onSelect: (screw: Screw) => void;
}

// Screw select, shown (and validated) only when the selected connector family is a screw one.
const ScrewField: FC<ScrewFieldProps> = ({ control, onSelect }) => {
  const i18n = useI18n();
  const screwsApi = useDeckScrewsApi();
  const screwOptions = useAsyncOptions(screwsApi.getDeckScrews);
  const connectorFamily = useWatch({ control, name: "connectorFamily" });

  if (!connectorFamily?.isScrew) {
    return null;
  }

  return (
    <Controller
      name="screw"
      control={control}
      rules={{ required: true }}
      render={({ field, fieldState }) => (
        <FormField label={i18n.translation.deckDesignerModule.screws.singular} error={fieldState.error}>
          {({ labelId }) => (
            <AsyncSelect<Screw, false>
              {...field}
              {...screwOptions}
              inputId={labelId}
              isMulti={false}
              placeholder={`${i18n.translation.common.select}...`}
              getOptionLabel={(option) => getItemOptionLabel(option.item)}
              getOptionValue={(option) => option.item.id}
              isInvalid={fieldState.invalid}
              onChange={(option) => {
                field.onChange(option ?? null);
                if (option) {
                  onSelect(option);
                }
              }}
            />
          )}
        </FormField>
      )}
    />
  );
};

const ConnectorForm: FC<ConnectorFormProps> = ({ connector, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const connectorsApi = useDeckConnectorsApi();
  const connectorFamiliesApi = useDeckConnectorFamiliesApi();
  const connectorMaterialsApi = useDeckConnectorMaterialsApi();
  const itemOptions = useItemOptions();
  const connectorFamilyOptions = useAsyncOptions(connectorFamiliesApi.getDeckConnectorFamilies);
  const materialOptions = useAsyncOptions(connectorMaterialsApi.getDeckConnectorMaterials);

  const defaultValues: ConnectorFormBody = {
    item: connector ? toItemOption(connector.item) : null,
    connectorFamily: connector?.connectorFamily ?? null,
    connectorMaterial: connector?.connectorMaterial ?? null,
    thickness: connector?.thickness ?? null,
    screw: connector?.connectorFamily.isScrew ? connector.screw : null,
    minGroovingHeight: connector?.minGroovingHeight ?? null,
    minBoardWidth: connector?.minBoardWidth ?? null,
  };

  const onSubmit = () => async (formData: ConnectorFormBody) => {
    if (
      !formData.item ||
      !formData.connectorFamily ||
      !formData.connectorMaterial ||
      formData.thickness == null ||
      (formData.connectorFamily.isScrew && !formData.screw)
    ) {
      return;
    }

    const body: InsertConnectorBody | UpdateConnectorBody = {
      itemId: formData.item.id,
      connectorFamilyCode: formData.connectorFamily.code,
      connectorMaterialId: formData.connectorMaterial.id,
      thickness: formData.thickness,
      screwId: formData.connectorFamily.isScrew ? (formData.screw?.item.id ?? null) : null,
      minGroovingHeight: formData.minGroovingHeight,
      minBoardWidth: formData.minBoardWidth,
    };

    try {
      if (connector) {
        await connectorsApi.updateDeckConnector(connector.item.id, body);
      } else {
        await connectorsApi.insertDeckConnector(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        connector
          ? `${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.connectors.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.deckDesignerModule.connectors.singular}`
      }
    >
      <Form<ConnectorFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {({ control, getValues, setValue }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<ItemOption, false>
                      {...field}
                      {...itemOptions}
                      inputId={labelId}
                      isMulti={false}
                      isDisabled={!!connector}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={getItemOptionLabel}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="connectorFamily"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.connectorFamilies.singular}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<ConnectorFamily, false>
                      {...field}
                      {...connectorFamilyOptions}
                      inputId={labelId}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => {
                        field.onChange(option);
                        if (!option?.isScrew) {
                          setValue("screw", null);
                        }
                      }}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="connectorMaterial"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.connectorMaterials.singular}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<ConnectorMaterial, false>
                      {...field}
                      {...materialOptions}
                      inputId={labelId}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="thickness"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.thickness} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      decimalScale={3}
                      suffix={" " + i18n.translation.common.unitMm}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="minGroovingHeight"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.connectors.minGroovingHeight}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      decimalScale={0}
                      suffix={" " + i18n.translation.common.unitMm}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="minBoardWidth"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.connectors.minBoardWidth}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      decimalScale={0}
                      suffix={" " + i18n.translation.common.unitMm}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <ScrewField
              control={control}
              onSelect={(screw) => {
                if (!getValues("item")) {
                  setValue("item", toItemOption(screw.item));
                }
              }}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default ConnectorForm;
